import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './components/ThemeProvider';
import { AuthProvider } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PageLoading } from './components/LoadingStates';
import { ProtectedRoute } from './components/ProtectedRoute';
import { usePageTitle, PAGE_CONFIGS } from './hooks/usePageTitle';

// Lazy load pages for better performance
const Login = lazy(() => import('./pages/Login').then(module => ({ default: module.Login })));
const UserManagement = lazy(() => import('./pages/UserManagement').then(module => ({ default: module.UserManagement })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const ResultsList = lazy(() => import('./pages/ResultsList').then(module => ({ default: module.ResultsList })));
const ProcessingStatus = lazy(() => import('./pages/ProcessingStatus').then(module => ({ default: module.ProcessingStatus })));
const CandidatesList = lazy(() => import('./pages/CandidatesList').then(module => ({ default: module.CandidatesList })));
const CandidateDetail = lazy(() => import('./pages/CandidateDetail').then(module => ({ default: module.CandidateDetail })));
const CandidateDrawings = lazy(() => import('./pages/CandidateDrawings').then(module => ({ default: module.CandidateDrawings })));
const UnmatchedDrawings = lazy(() => import('./pages/UnmatchedDrawings').then(module => ({ default: module.UnmatchedDrawings })));
const CandidateConflicts = lazy(() => import('./pages/CandidateConflicts').then(module => ({ default: module.CandidateConflicts })));
const CandidateCreation = lazy(() => import('./pages/CandidateCreation').then(module => ({ default: module.CandidateCreation })));
const EvaluationQuestions = lazy(() => import('./pages/EvaluationQuestions').then(module => ({ default: module.EvaluationQuestions })));
const ParameterAssignments = lazy(() => import('./pages/ParameterAssignments').then(module => ({ default: module.ParameterAssignments })));
const EvaluationStatus = lazy(() => import('./pages/EvaluationStatus').then(module => ({ default: module.EvaluationStatus })));
const LiveLogs = lazy(() => import('./components/LiveLogs').then(module => ({ default: module.LiveLogs })));
const CompetitionList = lazy(() => import('./components/CompetitionList'));
const CompetitionForm = lazy(() => import('./components/CompetitionForm'));
const CompetitionDetail = lazy(() => import('./components/CompetitionDetail'));
const Settings = lazy(() => import('./pages/Settings').then(module => ({ default: module.Settings })));
const NotFound = lazy(() => import('./pages/NotFound').then(module => ({ default: module.NotFound })));

// Page wrapper components that handle page titles
const LoginPage: React.FC = () => {
  usePageTitle({ title: 'Login', description: 'Sign in to your account' });
  return <Login />;
};

const UserManagementPage: React.FC = () => {
  usePageTitle({ title: 'User Management', description: 'Manage system users' });
  return <UserManagement />;
};

const DashboardPage: React.FC = () => {
  usePageTitle(PAGE_CONFIGS.dashboard);
  return <Dashboard />;
};

const ResultsListPage: React.FC = () => {
  usePageTitle(PAGE_CONFIGS.results);
  return <ResultsList />;
};


const ProcessingStatusPage: React.FC = () => {
  usePageTitle(PAGE_CONFIGS.processing);
  return <ProcessingStatus />;
};

const CandidatesListPage: React.FC = () => {
  usePageTitle({ title: 'All Candidates', description: 'View all candidates and their drawing status' });
  return <CandidatesList />;
};

const CandidateDetailPage: React.FC = () => {
  usePageTitle({ title: 'Candidate Details', description: 'View detailed candidate information' });
  return <CandidateDetail />;
};

const CandidateDrawingsPage: React.FC = () => {
  usePageTitle({ title: 'Candidate Drawings', description: 'View candidate drawings and evaluation status' });
  return <CandidateDrawings />;
};

const UnmatchedDrawingsPage: React.FC = () => {
  usePageTitle({ title: 'Unmatched Drawings', description: 'Manage drawings without matching candidates' });
  return <UnmatchedDrawings />;
};

const CandidateConflictsPage: React.FC = () => {
  usePageTitle({ title: 'Candidate Conflicts', description: 'Resolve duplicate candidate registration numbers' });
  return <CandidateConflicts />;
};

const CandidateCreationPage: React.FC = () => {
  usePageTitle({ title: 'Add Candidate', description: 'Create a new candidate record with conflict prevention' });
  return <CandidateCreation />;
};

const EvaluationQuestionsPage: React.FC = () => {
  usePageTitle({ title: 'Evaluation Questions', description: 'Manage AI evaluation questions and criteria' });
  return <EvaluationQuestions />;
};

const ParameterAssignmentsPage: React.FC = () => {
  usePageTitle({ title: 'Parameter Assignments', description: 'Manage participant parameter assignments' });
  return <ParameterAssignments />;
};

const EvaluationStatusPage: React.FC = () => {
  usePageTitle({ title: 'Evaluation Status', description: 'Monitor evaluation processing status' });
  return <EvaluationStatus />;
};


const CompetitionListPage: React.FC = () => {
  usePageTitle({ title: 'Competitions', description: 'Manage drawing competitions' });
  return <CompetitionList />;
};

const CompetitionFormPage: React.FC = () => {
  usePageTitle({ title: 'Create Competition', description: 'Create a new drawing competition' });
  return <CompetitionForm />;
};

const CompetitionDetailPage: React.FC = () => {
  usePageTitle({ title: 'Competition Details', description: 'View competition details and participants' });
  return <CompetitionDetail />;
};



const LiveLogsPage: React.FC = () => {
  usePageTitle({ title: 'Live Processing Logs', description: 'Real-time processing logs with OCR and AI evaluation details' });
  return <LiveLogs />;
};

const SettingsPage: React.FC = () => {
  usePageTitle({ title: 'Settings', description: 'Configure system settings' });
  return <Settings />;
};

const NotFoundPage: React.FC = () => {
  usePageTitle(PAGE_CONFIGS.notFound);
  return <NotFound />;
};

// Route configuration
const routes = [
  {
    path: '/login',
    element: <LoginPage />,
    errorBoundary: true,
    protected: false,
  },
  {
    path: '/users',
    element: <UserManagementPage />,
    errorBoundary: true,
    protected: true,
    requireAdmin: true,
  },
  {
    path: '/',
    element: <DashboardPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/results',
    element: <ResultsListPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/results/:registrationNumber',
    element: <CandidateDetailPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/processing',
    element: <ProcessingStatusPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/candidates',
    element: <CandidatesListPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/candidates/new',
    element: <CandidateCreationPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/candidates/:registrationNumber',
    element: <CandidateDetailPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/candidates/:registrationNumber/drawings',
    element: <CandidateDrawingsPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/candidates/:registrationNumber/edit',
    element: <CandidateCreationPage />, // Reuse creation page for editing
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/unmatched-drawings',
    element: <UnmatchedDrawingsPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/candidate-conflicts',
    element: <CandidateConflictsPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/competitions',
    element: <CompetitionListPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/competitions/create',
    element: <CompetitionFormPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/competitions/:id/edit',
    element: <CompetitionFormPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/competitions/:id',
    element: <CompetitionDetailPage />,
    errorBoundary: true,
    protected: true,
  },

  {
    path: '/evaluation/questions',
    element: <EvaluationQuestionsPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/evaluation/assignments',
    element: <ParameterAssignmentsPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/evaluation/status',
    element: <EvaluationStatusPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/logs',
    element: <LiveLogsPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '/settings',
    element: <SettingsPage />,
    errorBoundary: true,
    protected: true,
  },
  {
    path: '*',
    element: <NotFoundPage />,
    errorBoundary: false, // Don't wrap 404 page in error boundary
    protected: false,
  },
];

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ErrorBoundary>
          <Routes>
            {/* Login route without Layout */}
            <Route
              path="/login"
              element={
                <Suspense fallback={<PageLoading text="Loading page..." />}>
                  <LoginPage />
                </Suspense>
              }
            />
            
            {/* All other routes with Layout */}
            <Route
              path="*"
              element={
                <Layout>
                  <Suspense fallback={<PageLoading text="Loading page..." />}>
                    <Routes>
                      {routes
                        .filter(route => route.path !== '/login')
                        .map(({ path, element, errorBoundary, protected: isProtected, requireAdmin }) => (
                          <Route
                            key={path}
                            path={path}
                            element={
                              isProtected ? (
                                <ProtectedRoute requireAdmin={requireAdmin}>
                                  {errorBoundary ? (
                                    <ErrorBoundary>{element}</ErrorBoundary>
                                  ) : (
                                    element
                                  )}
                                </ProtectedRoute>
                              ) : errorBoundary ? (
                                <ErrorBoundary>{element}</ErrorBoundary>
                              ) : (
                                element
                              )
                            }
                          />
                        ))}
                    </Routes>
                  </Suspense>
                </Layout>
              }
            />
          </Routes>
        </ErrorBoundary>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
